import { statusReasonLabels, type LifecycleState, type StatusReason } from '@vigeo/shared';

/**
 * How a lifecycle state reads to HR — one registry, `satisfies Record<...>`, so adding a
 * state is a compile error here rather than a blank column somewhere.
 */
export interface LifecyclePresenter {
  readonly label: string;
  readonly description: string;
  /** Board column order. */
  readonly order: number;
  /** Whether this state belongs on the default pipeline board at all. */
  readonly onBoard: boolean;
}

export const lifecyclePresenters = {
  candidate: { label: 'Candidate', description: 'In the pipeline, not hired.', order: 0, onBoard: true },
  onboarding: { label: 'Onboarding', description: 'Offer accepted, working through requirements.', order: 1, onBoard: true },
  active: { label: 'Active', description: 'Employed and assignable.', order: 2, onBoard: true },
  inactive: { label: 'Inactive', description: 'Employed but not currently assignable.', order: 3, onBoard: false },
  separated: { label: 'Separated', description: 'No longer employed. Records retained.', order: 4, onBoard: false },
  withdrawn: { label: 'Withdrawn', description: 'Left the pipeline without being hired.', order: 5, onBoard: false },
} as const satisfies Record<LifecycleState, LifecyclePresenter>;

export const boardColumns = (Object.keys(lifecyclePresenters) as LifecycleState[])
  .filter((state) => lifecyclePresenters[state].onBoard)
  .sort((a, b) => lifecyclePresenters[a].order - lifecyclePresenters[b].order);

const SHORT_DAY = { year: 'numeric', month: 'short', day: 'numeric' } as const;

/** A calendar day HR typed (`YYYY-MM-DD`) as words. Read in UTC: the day was never a moment, so it must not slide to the evening before in the agency's zone. */
export const effectiveDayWords = (day: string): string => new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-US', { ...SHORT_DAY, timeZone: 'UTC' });

/**
 * What a status change carried, as one line: "On leave · took effect Oct 6, 2026 · back in March" (#772). The reason, the day it
 * took effect and the note, whichever were given; null when none were. The time it was RECORDED is not here: the history shows it.
 */
export function statusDetailLine(detail: { readonly reason: StatusReason | null; readonly effectiveOn: string | null; readonly note?: string | null } | null | undefined): string | null {
  if (detail === null || detail === undefined) return null;
  const parts = [
    detail.reason === null ? null : statusReasonLabels[detail.reason],
    detail.effectiveOn === null ? null : `took effect ${effectiveDayWords(detail.effectiveOn)}`,
    detail.note ?? null,
  ].filter((part): part is string => part !== null && part !== '');
  return parts.length === 0 ? null : parts.join(' · ');
}
